/** 个人中心-权益管理 */
import {
  BUY_TYPE,
  MAX_RESOLUTION,
  ORDER_TYPE,
  PROPERTY_RANGE,
  RANGE,
  SPEED,
  WATERMARK,
} from '@rendora/common/types/enums';
import { USER_LEVEL_KEY } from './auth';
import type { IDataBasic } from './common';

export {
  ORDER_BEHAVIOR,
  ORDER_TYPE,
  WATERMARK,
  MAX_RESOLUTION,
  RANGE,
  PROPERTY_RANGE,
  SPEED,
  BUY_TYPE,
  PAY_TYPE,
  SUBSCRIBE_STATUS,
  RIGHTS_FEATURE,
} from '@rendora/common/types/enums';

// PAY_STATUS: platform name for what common calls ORDER_STATUS.
// Common has 2 extra members (FINISHED=7, EXPIRED=99) — kept local to avoid widening.
export enum PAY_STATUS {
  UNPAID = 0, // 未付款
  PAID = 1, // 已付款
  CLOSE = 2, // 交易关闭
  REFUND = 3, // 已退款
  FAIL = 4, // 支付失败
  REVOKE = 5, // 已撤销
  PAYING = 6, // 支付中
}

export interface IAddPrice {
  add_account: number; // 加购一个子账号的价格
  add_account_original: number; // 加购一个子账号的原价格
  add_credit_coin: number; // 加购一个言币的价格
  add_render_pass: number; // 加购一个渲染通道的价格
  add_sublicense: number; // 加购一个转授权的价格
  add_storage: number; // 加购1GB存储空间的价格
  add_storage_original: number; // 加购1GB存储空间的原价格
  add_storage_t: number; // 加购1TB存储空间的价格
  add_storage_t_original: number; // 加购1TB存储空间的原价格
}
export interface IFees extends IDataBasic {
  buy_type: BUY_TYPE;
  add_price: {
    rights_template_id: number;
  } & IDataBasic &
    IAddPrice;
  time_identity: string; // “单月”/“单年”
  month_num: number; // 月数
  months: number; // 月数
  price: number; // 现价
  show_price: number; // 原价
  credit_coin: number; // 6 言币数量
  days: number; // 天
  rights_template_id: number;
  rights_time_id: number;
  upgrade_price?: number;
  upgrade_desc?: {
    credit_coin: number;
    from_rights_name: string;
    from_rights_price: number;
    to_rights_days: number;
    to_rights_name: string;
    to_rights_price: number;
    to_rights_remain_days: number;
    from_credit_coin: number;
    to_credit_coin: number;
    current_order_id: number;
    current_remained_credit_coin: number;
    total_fee: number;
    from_single_video_max_duration: number;
    to_single_video_max_duration: number;
  }[];
}

export interface IRightDisplay {
  type_description: string;
  detail_description: string;
  price_description: string;
  first_rights_title: string;
  first_content: string[];
  second_rights_title: string;
  second_content: string[];
  rights_template_id: number; // 主体套餐的id
}

export interface IEquity extends IDataBasic {
  account_count: number; // 4 席位数
  action_customize: number; // 是否可以咨询动作定制
  activity_coin: number;
  audio_limit: number; // 18-2 音频大小限制
  begin_end_video_customize: number; // 是否可以咨询片头片尾定制
  brand_guidelines: number; // 28 是否支持品牌规范
  cloth_customize: number; // 是否可以咨询服装定制
  description: string;
  exclusive_service: number; // 31 是否支持1v1专人服务
  exclusive_team: number; // 32 是否有专属售后团队
  add_more_credit_coins_limit: number; // 言币加购的上限
  faq: Array<
    IDataBasic & {
      question: string;
      answer: string;
    }
  >;
  hover?: string; // 额外文案
  file_upload_type: string; // 17 支持多种格式的素材上传
  font_limit: number; // 18-1 字体大小限制
  image_limit: number; // 图片限制
  licensor: number; // 3 授权主体数
  licensor_text: string; // 授权主体
  look_customize: number; // 16-1 是否可以咨询形象定制
  max_resolution: MAX_RESOLUTION; // 20 最高支持视频清晰度
  member_management: number; // 29 成员管理
  net_use: number; // 11 网站/应用程序/网络视频用途
  one_coin_time: number;
  outdoor_advertising: number; // 12 户外广告
  pag_limit: number; // pag限制
  pdf_limit: number; // pdf限制
  ppt_limit: number; // ppt限制
  privacy_protection: number; // 30 是否支持数据加密保护隐私
  publish: number; // 25 是否支持一键发布
  range: RANGE; // 9 授权范围
  recycle: number; // 23 回收站天数，单位天
  render_pass: number; // 5 渲染通道
  right_display: IRightDisplay;
  rights_by_time: IFees[];
  rights_name: string; // 套餐名称
  rights_type: USER_LEVEL_KEY & 24; // 套餐类型
  roles: PROPERTY_RANGE; // 13 三维形象库
  scene: PROPERTY_RANGE; // 14 三维场景库（场景）
  scene_customize: number; // 是否可以咨询场景定制
  single_video_max_duration: number; // 24 单条视频时长上限
  speed: SPEED; // 7 排队加速
  speed_up_coin: number;
  spread: number; // 10 新媒体平台及线上推广
  storage: number; //  19 云端存储空间
  storage_max: number; // 云端存储空间上限
  subject: string; // 1 主体说明
  team_space: number; // 27 团队空间
  team_work: number; // 26 团队多人协作
  tone: PROPERTY_RANGE; // 15 音色库
  video_limit: number; // 视频限制
  video_download_type: string; // 21 视频文件下载
  visual_property: number; // 是否支持团队视觉资产
  voice_customize: number; // 是否可以咨询声音定制
  watermark: WATERMARK; // 22 无水印&自定义水印
  work_permanent: number; // 2 是否作品永久授权
  work_sublicense: number; // 8 作品转授权主体数
}

// export interface ICompareContent {
//   hover?: string
//   subhover?: string
//   subtitle: string
//   title: string
//   value: any
// }

// export interface ICompareItem {
//   id: number
//   idx: number
//   subtitle: string
//   title: string
//   items: {
//     title: string
//     type: string
//     rights_content: {
//       [USER_LEVEL_KEY.TASTE]: ICompareContent
//       [USER_LEVEL_KEY.PERSONAL]: ICompareContent
//       [USER_LEVEL_KEY.TEAM_LIGHT]: ICompareContent
//       [SHOW_ORG_KEY.ORG_SHOW]: ICompareContent
//     }
//   }[]
// }

export interface IFaq {
  id: number;
  question: string;
  answer: string;
}

export interface IRightsCompareItem extends IDataBasic {
  title: string;
  type?: string;
  hover?: string;
  children?: {
    title: string;
    type?: string;
    rights_content?: {
      [key: string | number]: {
        hover?: string;
        title: string;
        subhover?: string;
        subtitle: string;
        value: boolean;
      };
    };
  }[];
  rights_content?: {
    [key: string | number]: {
      hover?: string;
      title: string;
      subhover?: string;
      subtitle: string;
      value: boolean;
    };
  };
}
export interface IRightsCompare extends IDataBasic {
  idx: number;
  title: string;
  subtitle: string;
  hover?: boolean;
  items: IRightsCompareItem[];
}
// export interface IRightDisaplyItem {
//   detail_description: string
//   first_content: string[]
//   first_rights_title: string
//   price_description: string
//   rights_template_id: number
//   second_rights_title: string
//   second_content: string[]
//   type_description: string
//   id: number
// // }
// export interface IFaqRight {
//   id: number
//   rights_name: string
//   rights_type: number
//   right_display: IRightDisplay
// }

export interface TRightDisplay {
  faq: IFaq[];
  rights: IEquity[];
  rights_compare: IRightsCompare[];
}

// 环状图数据结构
export interface IChartDataStat {
  remain: number;
  total: number;
}
export interface IChartData {
  [ORDER_TYPE.PACKAGE]: IChartDataStat;
  [ORDER_TYPE.ACTIVITY]: IChartDataStat;
  [ORDER_TYPE.CREDITS]: IChartDataStat;
}
